import { Injectable } from '@angular/core';

import { Observable } from 'rxjs/Observable';
import { Project } from '../../domain/models/project';
import { Job } from '../../domain/models/job';
import { RouteImport } from '../../domain/models/route-import';
import { RouteDraft } from '../../domain/models/routeDraft';

import { LoginDomainService } from '../../domain/services/login/login.domain.service';
import { JobDomainService } from '../../domain/services/job/job.domain.service';
import { Ordering } from '../../domain/enums/ordering';

import { ProjectOverviewViewModel } from '../viewModels/project-overview.viewModel';
import { Alert } from '../../domain/models/alert';
import { AlertDomainService } from '../../domain/services/alert/alert.domain.service';
import { QuotaDomainService } from '../../domain/services/quota/quota.domain.service';
import { TotalUserQuota } from '../../domain/models/TotalUserQuota';
import * as _ from 'lodash';
import { ConfigService } from '../../../utils/config-service';

@Injectable()
export class ProjectOverviewService {
  private lastOrdering: Ordering;

  constructor(
    private loginDomainService: LoginDomainService,
    private jobDomainService: JobDomainService,
    private alertDomainService: AlertDomainService,
    private quotaDomainService: QuotaDomainService,
    private configService: ConfigService) {
  }

  getProjects(): Observable<{} | ProjectOverviewViewModel[]> {
    return this.jobDomainService.getProjects()
      .map((projects: Project[]) => {
        return projects.map((project: Project) => new ProjectOverviewViewModel(project));
      });
  };

  createProject(projectName: string): Observable<{} | Project> {
    let project = new Project();
    project.name = projectName;

    return this.jobDomainService.createProject(project);
  }

  deleteProject(projectId: string): Observable<{} | any> {
    return this.jobDomainService.deleteProject(projectId);
  }

  deleteRoute(routeId: string): Observable<{} | any> {
    return this.jobDomainService.deleteJob(routeId);
  }

  stopRoute(routeId: string): Observable<{} | any> {
    return this.jobDomainService.stopJob(routeId);
  }

  getArchivedProjects(): Observable<{} | Array<ProjectOverviewViewModel>> {
    return this.jobDomainService.getAllArchivedProjects()
      .map((projects: Project[]) => {
        return projects.map((project: Project) => new ProjectOverviewViewModel(project));
      });
  }

  getUserId(): string {
    return this.loginDomainService.getUserId();
  }

  getOwnRoutes(): Observable<RouteImport[]> {
    let userId = this.getUserId();
    return this.jobDomainService.getAllJobs()
      .map((jobs: Job[]) => {
        let filteredJobs = _.filter(jobs, (job: Job) => {
          return job.userId === userId;
        });
        let routes = filteredJobs.map((job: Job) => new RouteImport(job));
        return routes;
      });
  };

  archiveProject(project: ProjectOverviewViewModel): Observable<string> {
    return this.jobDomainService.archiveProject(project.id, !project.archived);
  }

  shareProject(project: ProjectOverviewViewModel): Observable<string> {
    return this.jobDomainService.shareProject(project.id, true).map((key) => this.configService.getEnv('jobApi').replace('job', 'viewer') + 'project/' + key);
  }

  getSharingLink(key: string): string {
    return this.configService.getEnv('jobApi').replace('job', 'viewer') + 'project/' + key;
  }

  unshareProject(project: ProjectOverviewViewModel): Observable<string> {
    return this.jobDomainService.shareProject(project.id, false);
  }

  setLastOrderMethod(ordering: Ordering) {
    this.lastOrdering = ordering;
  }

  getLastOrderMethod(): Ordering {
    return this.lastOrdering;
  }

  addJobToProject(route: RouteImport, projectId: string): Observable<any> {
    return this.jobDomainService.addJobToProject(route.id, route.toRemove ? null : projectId);
  }

  getAlertsForUser(): Observable<{} | Alert[]> {
    return this.alertDomainService.getAlertsByUser();
  }

  getMaxQuota(): Observable<{} | TotalUserQuota> {
    return this.quotaDomainService.getTotalQuota();
  }

  getDraftRoutes(): RouteDraft[] {
    return this.jobDomainService.getDraftRoutesFromLocalStorage();
  }

  saveDraftRoutes(draftRoutes: RouteDraft[]): void {
    this.jobDomainService.saveDraftRoutesToLocalStorage(draftRoutes);
  }

  deleteRouteDraft(jobId: string): void {
    this.jobDomainService.deleteRouteDraftFromLocalStorage(jobId);
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/project-overview/services/project-overview.service.ts